import { useState, useEffect, useRef, useCallback } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { useNavigate, Link } from "react-router-dom";
import {
  FaTimes,
  FaUser,
  FaBell,
  FaClipboardList,
  FaPencilAlt,
  FaUserFriends,
} from "react-icons/fa";
import {
  useProfileMutation,
  useCloseAccountMutation,
  useLogoutMutation,
  useLazyGetProfileQuery,
  useGetUserNotificationsQuery,
  useDeleteBusinessStripeMethodMutation,
  useStartStripeOnboardingMutation,
  useSyncStripeMerchantStatusMutation,
  useGetFollowingCreatorsQuery,
  useUnfollowCreatorMutation,
} from "../slices/usersApiSlice.js";
import { setCredentials, logout } from "../slices/authSlice.js";
import { useGetMyOrdersQuery } from "../slices/ordersApiSlice.js";
import { formatPrice } from "../utils/formatPrice.js";
import { useGetExchangeRatesQuery } from "../slices/exchangeSlice.js";

function ProfileScreen() {
  const [activeSection, setActiveSection] = useState("profile");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmModalAction, setConfirmModalAction] = useState(null);
  const [isDeletingStripeCredentials, setIsDeletingStripeCredentials] = useState(false);

  const { userInfo } = useSelector((state) => state.auth);
  const { data: exchangeData } = useGetExchangeRatesQuery();
  const preferredCurrency = userInfo?.preferredCurrency || "USD";

  const { data, isLoading: isNotifying, error: errorNotify } = useGetUserNotificationsQuery(userInfo._id);
  const {
    data: followingCreators = [],
    isLoading: isFollowingLoading,
    error: followingError,
    refetch: refetchFollowing,
  } = useGetFollowingCreatorsQuery(undefined, { skip: !userInfo });
  const [unfollowCreator, { isLoading: unfollowingBusy }] = useUnfollowCreatorMutation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [updateProfile, { isLoading: loadingUpdateProfile }] = useProfileMutation();
  const [closeAccount, { isLoading: isClosingAccount }] = useCloseAccountMutation();
  const [logoutApiCall] = useLogoutMutation();
  const [triggerProfile] = useLazyGetProfileQuery();
  const { data: orders = [], isLoading, error } = useGetMyOrdersQuery();

  const [deleteBusinessStripeMethod] = useDeleteBusinessStripeMethodMutation();
  const [startStripeOnboarding] = useStartStripeOnboardingMutation();
  const [syncStripeMerchantStatus] = useSyncStripeMerchantStatusMutation();
  const ranStripe = useRef(false);

  const refreshAuthProfile = useCallback(async () => {
    const refreshedProfile = await triggerProfile(undefined, false).unwrap();
    dispatch(setCredentials(refreshedProfile));
    return refreshedProfile;
  }, [triggerProfile, dispatch]);

  const creatorStripe = userInfo?.businessMethods?.find(
    (m) => m.type === "stripe" && m.stripe?.accountId
  )?.stripe;

  useEffect(() => {
    if (userInfo) {
      setName(userInfo.name);
      setEmail(userInfo.email);
    }
  }, [userInfo, userInfo?.name, userInfo?.email]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeState = urlParams.get("stripe");
    if (stripeState === "success" && !ranStripe.current) {
      ranStripe.current = true;
      (async () => {
        try {
          await syncStripeMerchantStatus().unwrap();
          await refreshAuthProfile();
          toast.success("Stripe onboarding completed!");
          navigate("/profile", { replace: true });
          setActiveSection("paymentMethod");
        } catch (err) {
          toast.error(err?.data?.message || "Failed to sync Stripe merchant info");
        }
      })();
    }
  }, [syncStripeMerchantStatus, refreshAuthProfile, navigate]);

  const maskMerchantId = (merchantId) => {
    if (!merchantId || merchantId.length < 4) return "****";
    const visiblePart = merchantId.slice(-4);
    const maskedPart = "*".repeat(merchantId.length - 4);
    return `${maskedPart}${visiblePart}`;
  };

  async function submitHandler(e) {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      const res = await updateProfile({
        _id: userInfo._id,
        name,
        email,
        password,
      }).unwrap();
      dispatch(setCredentials(res));
      toast.success("Profile updated successfully");
    } catch (submitErr) {
      toast.error(submitErr?.data?.message || submitErr.error);
    }
  }

  const closeAccountHandler = async () => {
    try {
      await closeAccount().unwrap();
      await logoutApiCall().unwrap();
      dispatch(logout());
      toast.success("Account closed. You can reactivate it by logging in again.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to close account");
    }
  };

  const deleteStripeCredentialsHandler = async () => {
    try {
      setIsDeletingStripeCredentials(true);
      await deleteBusinessStripeMethod().unwrap();
      await refreshAuthProfile();
      toast.success("Stripe credentials deleted");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete Stripe credentials");
    } finally {
      setIsDeletingStripeCredentials(false);
    }
  };

  const closeConfirmModal = () => {
    if (isClosingAccount || isDeletingStripeCredentials) return;
    setConfirmModalAction(null);
  };

  const confirmModalSubmit = async () => {
    if (confirmModalAction === "close-account") {
      await closeAccountHandler();
      return;
    }
    if (confirmModalAction === "delete-stripe") {
      await deleteStripeCredentialsHandler();
      setConfirmModalAction(null);
    }
  };

  const navItems = [
    { key: "profile", label: "User Profile", icon: FaUser },
    { key: "notifications", label: "Notifications", icon: FaBell },
    { key: "orders", label: "My Orders", icon: FaClipboardList },
    { key: "paymentMethod", label: "My Payment Methods", icon: FaPencilAlt },
    { key: "following", label: "Following", icon: FaUserFriends },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <aside className="lg:col-span-3">
        <nav className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activeSection === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveSection(item.key)}
                className={`mb-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                  active
                    ? "bg-sky-100 text-sky-700"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <Icon className="text-sm" />
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <main className="space-y-5 lg:col-span-9">
        {activeSection === "profile" && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-2xl font-semibold text-slate-900">User Profile</h2>
            <form onSubmit={submitHandler} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
                <input
                  type="text"
                  placeholder="Enter name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Email Address</label>
                <input
                  type="email"
                  placeholder="Enter Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Password</label>
                <input
                  type="password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Confirm Password</label>
                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="submit"
                  className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmModalAction("close-account")}
                  disabled={isClosingAccount}
                  className="rounded-xl border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isClosingAccount ? "Closing..." : "Close Account"}
                </button>
              </div>
              <p className="text-xs leading-5 text-slate-500">
                Closing your account hides it from active use, but you can reactivate it later by
                signing in again.
              </p>
              {loadingUpdateProfile && <Loader />}
            </form>
          </section>
        )}

        {activeSection === "notifications" && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-2xl font-semibold text-slate-900">Notifications</h2>
            {isNotifying ? (
              <Loader />
            ) : errorNotify ? (
              <Message variant="danger">
                {errorNotify?.data?.message || "Error loading notifications"}
              </Message>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="px-2 py-2 font-semibold">Subject</th>
                      <th className="px-2 py-2 font-semibold">Content</th>
                      <th className="px-2 py-2 font-semibold">Date</th>
                      <th className="px-2 py-2 font-semibold">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.notifications?.length > 0 ? (
                      data.notifications.map((notification) => (
                        <tr key={notification._id} className="border-b border-slate-100 text-slate-700">
                          <td className="px-2 py-2">{notification.subject}</td>
                          <td className="px-2 py-2">{notification.content.substring(0, 30)}...</td>
                          <td className="px-2 py-2">{format(new Date(notification.createdAt), "yyyy-MM-dd")}</td>
                          <td className="px-2 py-2">{format(new Date(notification.createdAt), "HH:mm:ss")}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-2 py-4 text-center text-slate-500">
                          No notifications found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {activeSection === "orders" && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-2xl font-semibold text-slate-900">My Orders</h2>
            {isLoading ? (
              <Loader />
            ) : error ? (
              <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="px-2 py-2 font-semibold">ID</th>
                      <th className="px-2 py-2 font-semibold">DATE</th>
                      <th className="px-2 py-2 font-semibold">TOTAL</th>
                      <th className="px-2 py-2 font-semibold">PAID</th>
                      <th className="px-2 py-2 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id} className="border-b border-slate-100 text-slate-700">
                        <td className="px-2 py-2">{order._id}</td>
                        <td className="px-2 py-2">{order.createdAt?.substring(0, 10)}</td>
                        <td className="px-2 py-2">
                          {formatPrice(order.totalPrice, preferredCurrency, exchangeData)}
                        </td>
                        <td className="px-2 py-2">
                          {order.isPaid ? order.paidAt?.substring(0, 10) : <FaTimes className="text-rose-600" />}
                        </td>
                        <td className="px-2 py-2">
                          <Link
                            to={`/order/${order._id}`}
                            className="inline-flex rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 no-underline hover:bg-slate-50"
                          >
                            Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {activeSection === "paymentMethod" && (
          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-2xl font-semibold text-slate-900">Creator Payment Methods</h2>
              {!userInfo?.isCreator ? (
                <>
                  <p className="mb-3 text-sm text-slate-600">
                    Become a creator to start uploading your own work and earn money!
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/profile/become-a-creator")}
                    className="rounded-xl border border-sky-300 bg-white px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50"
                  >
                    Become a Creator
                  </button>
                </>
              ) : userInfo?.isPlatformAccount ? (
                <p className="text-sm text-slate-600">
                  This is a platform account. Stripe Connect onboarding is not required because payouts
                  are routed to the platform balance by default.
                </p>
              ) : !creatorStripe?.accountId ? (
                <>
                  <button
                    type="button"
                    className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900"
                    onClick={async () => {
                      try {
                        const { redirectUrl } = await startStripeOnboarding().unwrap();
                        window.location.href = redirectUrl;
                      } catch (err) {
                        toast.error(err?.data?.message || "Failed to start Stripe onboarding");
                      }
                    }}
                  >
                    Connect with Stripe
                  </button>
                  <p className="mt-3 text-xs leading-5 text-slate-500">
                    Stripe credentials are used to route creator payments. Until they are connected,
                    Stripe payouts cannot be processed for this profile.
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-2 text-sm text-slate-700">
                    Stripe Account ID: <strong>{maskMerchantId(creatorStripe.accountId)}</strong>
                  </p>
                  <p className="mb-1 text-sm text-slate-600">
                    Onboarding Status: <strong>{creatorStripe.onboardingStatus || "UNKNOWN"}</strong>
                  </p>
                  <p className="mb-1 text-sm text-slate-600">
                    Charges Enabled: <strong>{creatorStripe.chargesEnabled ? "Yes" : "No"}</strong>
                  </p>
                  <p className="mb-3 text-sm text-slate-600">
                    Payouts Enabled: <strong>{creatorStripe.payoutsEnabled ? "Yes" : "No"}</strong>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900"
                      onClick={async () => {
                        try {
                          const { redirectUrl } = await startStripeOnboarding().unwrap();
                          window.location.href = redirectUrl;
                        } catch (err) {
                          toast.error(err?.data?.message || "Failed to continue Stripe onboarding");
                        }
                      }}
                    >
                      Continue Stripe Onboarding
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      onClick={async () => {
                        try {
                          await syncStripeMerchantStatus().unwrap();
                          await refreshAuthProfile();
                          toast.success("Stripe status synced");
                        } catch (err) {
                          toast.error(err?.data?.message || "Failed to sync Stripe status");
                        }
                      }}
                    >
                      Sync Stripe Status
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                      onClick={() => setConfirmModalAction("delete-stripe")}
                      disabled={isDeletingStripeCredentials}
                    >
                      {isDeletingStripeCredentials ? "Deleting..." : "Delete Stripe Credentials"}
                    </button>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-slate-500">
                    Deleting Stripe credentials disconnects payouts for this creator profile until
                    Stripe is connected again.
                  </p>
                </>
              )}
            </div>
          </section>
        )}

        {activeSection === "following" && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-2xl font-semibold text-slate-900">Following Creators</h2>
            {isFollowingLoading ? (
              <Loader />
            ) : followingError ? (
              <Message variant="danger">
                {followingError?.data?.message || "Failed to load followed creators"}
              </Message>
            ) : followingCreators.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <p className="m-0">You are not following any creators yet.</p>
                <p className="mt-2 mb-0 text-xs leading-5 text-slate-500">
                  Following helps you keep track of creators you want to revisit, and their updates
                  appear through your account activity and creator pages.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="px-2 py-2 font-semibold">Name</th>
                      <th className="px-2 py-2 font-semibold">Status</th>
                      <th className="px-2 py-2 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {followingCreators.map((creator) => {
                      const creatorTag = creator?.creator?.tag;
                      return (
                        <tr key={creator._id} className="border-b border-slate-100 text-slate-700">
                          <td className="px-2 py-2">
                            {creator?.creator?.name || creator?.name || "Creator"}
                          </td>
                          <td className="px-2 py-2">{creator?.closed ? "Unavailable" : "Active"}</td>
                          <td className="px-2 py-2">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={!creatorTag}
                                onClick={() => navigate(`/user/creator/${creatorTag}`)}
                                className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                View
                              </button>
                              <button
                                type="button"
                                disabled={unfollowingBusy}
                                onClick={async () => {
                                  try {
                                    await unfollowCreator(creator._id).unwrap();
                                    toast.success("Creator unfollowed");
                                    refetchFollowing();
                                  } catch (err) {
                                    toast.error(err?.data?.message || "Failed to unfollow creator");
                                  }
                                }}
                                className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Unfollow
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>

      {confirmModalAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={closeConfirmModal}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Confirm action"
          >
            <h3 className="text-lg font-semibold text-slate-900">
              {confirmModalAction === "close-account" ? "Close Account" : "Delete Stripe Credentials"}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {confirmModalAction === "close-account"
                ? "Are you sure you want to close your account? You can reactivate it later by logging in again."
                : "Delete Stripe Connect credentials from this profile? This will disable Stripe payouts until you reconnect."}
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeConfirmModal}
                disabled={isClosingAccount || isDeletingStripeCredentials}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmModalSubmit}
                disabled={isClosingAccount || isDeletingStripeCredentials}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isClosingAccount
                  ? "Closing..."
                  : isDeletingStripeCredentials
                  ? "Deleting..."
                  : confirmModalAction === "close-account"
                  ? "Close Account"
                  : "Delete Credentials"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileScreen;

